// Agency Playbook Management - Super Admin Only
// Allows adding, editing, and deleting federal agencies + their bureaus

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Building2,
  RefreshCw,
  Loader2,
  Network,
  Check,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENT_TYPES = [
  { value: 'Cabinet',      label: 'Cabinet Department' },
  { value: 'Independent',  label: 'Independent Agency' },
  { value: 'Intelligence', label: 'Intelligence Community' },
  { value: 'Military',     label: 'Military Branch' },
  { value: 'Other',        label: 'Other' },
];

const EMOJI_OPTIONS = [
  '🛡️', '⚖️', '🏛️', '🚀', '🎖️', '🏥', '⚡', '🚗', '🌐', '🌾',
  '🏞️', '👷', '📚', '🏠', '💰', '🌿', '🏢', '📈', '👴', '🤝',
  '🔬', '⚓', '✈️', '🎯', '🔒', '📡', '🌊', '🏭', '⚙️', '🎨',
];

const EMPTY_FORM = {
  code: '', abbreviation: '', name: '', logo_emoji: '🏛️',
  department_type: '', headquarters: '', annual_budget: '',
  historical_win_rate: '', avg_contract_size: '',
  procurement_portal_url: '', sam_url: '', evaluation_criteria_notes: '',
};

export default function AgencyPlaybook() {
  const { getAuthHeaders } = useAuth();

  // ── Agency list state ────────────────────────────────────────────────────
  const [agencies,     setAgencies]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');

  // ── Agency edit/create modal ─────────────────────────────────────────────
  const [isModalOpen,    setIsModalOpen]    = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [formData,       setFormData]       = useState(EMPTY_FORM);

  // ── Bureau state (only populated when editing an existing agency) ─────────
  const [bureaus,           setBureaus]           = useState([]);
  const [loadingBureaus,    setLoadingBureaus]    = useState(false);
  const [bureauForm,        setBureauForm]        = useState({ code: '', name: '' });
  const [savingBureau,      setSavingBureau]      = useState(false);
  const [editingBureauId,   setEditingBureauId]   = useState(null);
  const [editBureauForm,    setEditBureauForm]    = useState({ code: '', name: '' });

  // ── Agency fetch ─────────────────────────────────────────────────────────
  const fetchAgencies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agencies`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAgencies(data.items || []);
      } else {
        toast.error('Failed to load agencies');
      }
    } catch {
      toast.error('Failed to load agencies');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAgencies(); }, [fetchAgencies]);

  // ── Bureau fetch ─────────────────────────────────────────────────────────
  const fetchBureaus = useCallback(async (agencyCode) => {
    if (!agencyCode) return;
    setLoadingBureaus(true);
    try {
      const res = await fetch(
        `${API_URL}/api/agencies/bureaus?agency_code=${encodeURIComponent(agencyCode)}`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) setBureaus(await res.json());
    } catch {
      // non-fatal
    } finally {
      setLoadingBureaus(false);
    }
  }, [getAuthHeaders]);

  // Re-fetch bureaus whenever we open the modal on an existing agency
  useEffect(() => {
    if (isModalOpen && selectedAgency) {
      fetchBureaus(selectedAgency.code);
    } else {
      setBureaus([]);
      setBureauForm({ code: '', name: '' });
      setEditingBureauId(null);
    }
  }, [isModalOpen, selectedAgency, fetchBureaus]);

  // ── Agency open modal ────────────────────────────────────────────────────
  const handleOpenModal = (agency = null) => {
    if (agency) {
      setSelectedAgency(agency);
      setFormData({
        code:                      agency.code || '',
        abbreviation:              agency.abbreviation || '',
        name:                      agency.name || '',
        logo_emoji:                agency.logo_emoji || '🏛️',
        department_type:           agency.department_type || '',
        headquarters:              agency.headquarters || '',
        annual_budget:             agency.annual_budget || '',
        historical_win_rate:       agency.historical_win_rate?.toString() || '',
        avg_contract_size:         agency.avg_contract_size || '',
        procurement_portal_url:    agency.procurement_portal_url || '',
        sam_url:                   agency.sam_url || '',
        evaluation_criteria_notes: agency.evaluation_criteria_notes || '',
      });
    } else {
      setSelectedAgency(null);
      setFormData(EMPTY_FORM);
    }
    setIsModalOpen(true);
  };

  // ── Agency save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Code and Name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        historical_win_rate: formData.historical_win_rate
          ? parseFloat(formData.historical_win_rate)
          : null,
      };
      const url    = selectedAgency
        ? `${API_URL}/api/agencies/${selectedAgency.id}`
        : `${API_URL}/api/agencies`;
      const method = selectedAgency ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(selectedAgency ? 'Agency updated!' : 'Agency created!');
        setIsModalOpen(false);
        fetchAgencies();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save agency');
      }
    } catch {
      toast.error('Failed to save agency');
    } finally {
      setSaving(false);
    }
  };

  // ── Agency delete ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!selectedAgency) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/agencies/${selectedAgency.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Agency deleted');
        setIsDeleteModalOpen(false);
        setSelectedAgency(null);
        fetchAgencies();
      } else {
        toast.error('Failed to delete agency');
      }
    } catch {
      toast.error('Failed to delete agency');
    } finally {
      setSaving(false);
    }
  };

  // ── Bureau add ───────────────────────────────────────────────────────────
  const handleAddBureau = async () => {
    if (!bureauForm.code.trim() || !bureauForm.name.trim()) {
      toast.error('Bureau code and name are required');
      return;
    }
    setSavingBureau(true);
    try {
      const res = await fetch(`${API_URL}/api/agencies/bureaus`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code:               bureauForm.code.trim().toUpperCase(),
          name:               bureauForm.name.trim(),
          parent_agency_code: selectedAgency.code,
        }),
      });
      if (res.ok) {
        toast.success('Bureau added');
        setBureauForm({ code: '', name: '' });
        fetchBureaus(selectedAgency.code);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add bureau');
      }
    } catch {
      toast.error('Failed to add bureau');
    } finally {
      setSavingBureau(false);
    }
  };

  // ── Bureau update ────────────────────────────────────────────────────────
  const handleUpdateBureau = async (bureauId) => {
    setSavingBureau(true);
    try {
      const res = await fetch(`${API_URL}/api/agencies/bureaus/${bureauId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: editBureauForm.code.trim().toUpperCase(),
          name: editBureauForm.name.trim(),
        }),
      });
      if (res.ok) {
        toast.success('Bureau updated');
        setEditingBureauId(null);
        fetchBureaus(selectedAgency.code);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update bureau');
      }
    } catch {
      toast.error('Failed to update bureau');
    } finally {
      setSavingBureau(false);
    }
  };

  // ── Bureau delete ────────────────────────────────────────────────────────
  const handleDeleteBureau = async (bureauId, bureauCode) => {
    try {
      const res = await fetch(`${API_URL}/api/agencies/bureaus/${bureauId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success(`Bureau ${bureauCode} removed`);
        fetchBureaus(selectedAgency.code);
      } else {
        toast.error('Failed to remove bureau');
      }
    } catch {
      toast.error('Failed to remove bureau');
    }
  };

  // ── Filtered list ────────────────────────────────────────────────────────
  const filteredAgencies = agencies.filter((a) =>
    a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6" data-testid="agency-playbook-page">

      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Agency Playbook</h1>
          <p className="text-slate-500 mt-1">
            Manage federal agency intelligence data for pursuit analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchAgencies} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => handleOpenModal()} data-testid="btn-add-agency">
            <Plus className="w-4 h-4 mr-2" />
            Add Agency
          </Button>
        </div>
      </div>

      {/* ── Search & stats ───────────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search agencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="agency-search"
          />
        </div>
        <Badge variant="secondary" className="text-sm">{agencies.length} agencies</Badge>
      </div>

      {/* ── Agencies table ───────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredAgencies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No agencies found</p>
              <Button variant="outline" className="mt-4" onClick={() => handleOpenModal()}>
                <Plus className="w-4 h-4 mr-2" />Add First Agency
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Icon</TableHead>
                  <TableHead className="w-20">Code</TableHead>
                  <TableHead className="w-20">ABBRV</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Win Rate</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAgencies.map((agency) => (
                  <TableRow key={agency.id} data-testid={`agency-row-${agency.code}`}>
                    <TableCell>
                      <span className="text-2xl">{agency.logo_emoji || '🏛️'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{agency.code}</Badge>
                    </TableCell>
                    <TableCell>
                      {agency.abbreviation ? (
                        <Badge variant="secondary" className="font-mono text-[10px]">
                          {agency.abbreviation}
                        </Badge>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{agency.name}</TableCell>
                    <TableCell>
                      {agency.department_type && (
                        <Badge variant="secondary">{agency.department_type}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.historical_win_rate ? (
                        <span className="text-emerald-600 font-medium">
                          {agency.historical_win_rate}%
                        </span>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.annual_budget || <span className="text-slate-400">N/A</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(agency)}
                          data-testid={`btn-edit-${agency.code}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => { setSelectedAgency(agency); setIsDeleteModalOpen(true); }}
                          data-testid={`btn-delete-${agency.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════
          Add / Edit Agency Modal
      ═══════════════════════════════════════════════════════════════ */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedAgency ? 'Edit Agency' : 'Add New Agency'}
            </DialogTitle>
            <DialogDescription>
              {selectedAgency
                ? 'Update agency information and manage its bureaus / sub-departments'
                : 'Add a new federal agency to the playbook'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">

            {/* Row 1: Emoji, Code, Abbreviation, Name */}
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-2">
                <Label>Icon</Label>
                <Select
                  value={formData.logo_emoji}
                  onValueChange={(v) => setFormData({ ...formData, logo_emoji: v })}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <span className="text-xl">{formData.logo_emoji}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <div className="grid grid-cols-5 gap-1 p-2">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          className="p-2 text-xl hover:bg-slate-100 rounded cursor-pointer"
                          onClick={() => setFormData({ ...formData, logo_emoji: emoji })}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Code *</Label>
                <Input
                  placeholder="NN"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={!!selectedAgency}
                  maxLength={4}
                  className="font-mono"
                  data-testid="input-agency-code"
                />
                <p className="text-[10px] text-slate-400 mt-1">2-letter internal</p>
              </div>
              <div className="col-span-2">
                <Label>ABBRV</Label>
                <Input
                  placeholder="NASA"
                  value={formData.abbreviation}
                  onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value.toUpperCase() })}
                  maxLength={6}
                  className="font-mono"
                  data-testid="input-agency-abbreviation"
                />
                <p className="text-[10px] text-slate-400 mt-1">3–5 letter public</p>
              </div>
              <div className="col-span-6">
                <Label>Full Name *</Label>
                <Input
                  placeholder="National Aeronautics and Space Administration"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="input-agency-name"
                />
              </div>
            </div>

            {/* Row 2: Department Type, Headquarters */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department Type</Label>
                <Select
                  value={formData.department_type}
                  onValueChange={(v) => setFormData({ ...formData, department_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Headquarters</Label>
                <Input
                  placeholder="Washington, DC"
                  value={formData.headquarters}
                  onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
                />
              </div>
            </div>

            {/* Row 3: Budget, Win Rate, Avg Contract */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Annual Budget</Label>
                <Input
                  placeholder="$850B"
                  value={formData.annual_budget}
                  onChange={(e) => setFormData({ ...formData, annual_budget: e.target.value })}
                />
              </div>
              <div>
                <Label>Historical Win Rate (%)</Label>
                <Input
                  type="number"
                  placeholder="35"
                  min="0"
                  max="100"
                  value={formData.historical_win_rate}
                  onChange={(e) => setFormData({ ...formData, historical_win_rate: e.target.value })}
                />
              </div>
              <div>
                <Label>Avg Contract Size</Label>
                <Input
                  placeholder="$5M – $50M"
                  value={formData.avg_contract_size}
                  onChange={(e) => setFormData({ ...formData, avg_contract_size: e.target.value })}
                />
              </div>
            </div>

            {/* Row 4: Portal URLs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Procurement Portal URL</Label>
                <Input
                  placeholder="https://..."
                  value={formData.procurement_portal_url}
                  onChange={(e) => setFormData({ ...formData, procurement_portal_url: e.target.value })}
                />
              </div>
              <div>
                <Label>SAM.gov URL</Label>
                <Input
                  placeholder="https://sam.gov/..."
                  value={formData.sam_url}
                  onChange={(e) => setFormData({ ...formData, sam_url: e.target.value })}
                />
              </div>
            </div>

            {/* Row 5: Evaluation Notes */}
            <div>
              <Label>Evaluation Criteria Notes</Label>
              <Textarea
                placeholder="Key evaluation factors, LPTA vs Best Value preference, common disqualification reasons…"
                value={formData.evaluation_criteria_notes}
                onChange={(e) => setFormData({ ...formData, evaluation_criteria_notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* ── Bureaus / Sub-Departments (edit mode only) ──────────── */}
            {selectedAgency && (
              <div className="border-t border-slate-200 pt-5 mt-1">

                {/* Section header */}
                <div className="flex items-center gap-2 mb-4">
                  <Network className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">
                    Bureaus / Sub-Departments
                  </span>
                  {bureaus.length > 0 && (
                    <Badge variant="secondary" className="text-xs">{bureaus.length}</Badge>
                  )}
                  <span className="text-xs text-slate-400 ml-1">
                    ({selectedAgency.code} → sub-codes)
                  </span>
                </div>

                {/* Existing bureau list */}
                {loadingBureaus ? (
                  <div className="flex items-center gap-2 py-3 text-sm text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading bureaus…
                  </div>
                ) : bureaus.length === 0 ? (
                  <p className="text-xs text-slate-400 italic mb-4 ml-1">
                    No bureaus yet — add one below.
                  </p>
                ) : (
                  <div className="space-y-1.5 mb-4 max-h-52 overflow-y-auto pr-1">
                    {bureaus.map((b) =>
                      editingBureauId === b.id ? (
                        /* ── Inline edit row ── */
                        <div
                          key={b.id}
                          className="flex items-center gap-2 p-2 border border-blue-200 bg-blue-50 rounded-lg"
                        >
                          <Input
                            value={editBureauForm.code}
                            onChange={(e) =>
                              setEditBureauForm({ ...editBureauForm, code: e.target.value.toUpperCase() })
                            }
                            className="w-24 font-mono text-xs h-8"
                            maxLength={8}
                            placeholder="Code"
                            data-testid="input-bureau-edit-code"
                          />
                          <Input
                            value={editBureauForm.name}
                            onChange={(e) =>
                              setEditBureauForm({ ...editBureauForm, name: e.target.value })
                            }
                            className="flex-1 text-xs h-8"
                            placeholder="Bureau name"
                            data-testid="input-bureau-edit-name"
                          />
                          <Button
                            size="sm"
                            className="h-8 px-2 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleUpdateBureau(b.id)}
                            disabled={savingBureau}
                            data-testid="btn-bureau-save"
                          >
                            {savingBureau
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Check className="w-3.5 h-3.5" />
                            }
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-slate-500"
                            onClick={() => setEditingBureauId(null)}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        /* ── Read-only row ── */
                        <div
                          key={b.id}
                          className="flex items-center gap-3 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 group transition-colors"
                        >
                          <Badge variant="outline" className="font-mono text-xs shrink-0">
                            {b.code}
                          </Badge>
                          <span className="flex-1 text-sm text-slate-700 truncate">{b.name}</span>
                          {/* action buttons — always visible */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600"
                              onClick={() => {
                                setEditingBureauId(b.id);
                                setEditBureauForm({ code: b.code, name: b.name });
                              }}
                              data-testid={`btn-edit-bureau-${b.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteBureau(b.id, b.code)}
                              data-testid={`btn-delete-bureau-${b.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

                {/* Add-bureau form */}
                <div className="flex items-end gap-2 p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg">
                  <div className="w-28 shrink-0">
                    <Label className="text-xs text-slate-500 mb-1 block">Bureau Code</Label>
                    <Input
                      placeholder={`${selectedAgency.code}05`}
                      value={bureauForm.code}
                      onChange={(e) => setBureauForm({ ...bureauForm, code: e.target.value.toUpperCase() })}
                      className="font-mono text-xs h-8"
                      maxLength={8}
                      data-testid="input-bureau-code"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 mb-1 block">Bureau / Department Name</Label>
                    <Input
                      placeholder="e.g. Bureau of Land Management"
                      value={bureauForm.name}
                      onChange={(e) => setBureauForm({ ...bureauForm, name: e.target.value })}
                      className="text-xs h-8"
                      data-testid="input-bureau-name"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddBureau(); }}
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={handleAddBureau}
                    disabled={savingBureau || !bureauForm.code.trim() || !bureauForm.name.trim()}
                    className="h-8 bg-slate-800 hover:bg-slate-900 text-white shrink-0"
                    data-testid="btn-add-bureau"
                  >
                    {savingBureau
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><Plus className="w-3.5 h-3.5 mr-1" />Add</>
                    }
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              {selectedAgency ? 'Close' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-agency">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                selectedAgency ? 'Save Changes' : 'Create Agency'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Agency Confirmation ────────────────────────────────── */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Agency</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <strong>{selectedAgency?.name}</strong> ({selectedAgency?.code})?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
              data-testid="btn-confirm-delete"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                'Delete Agency'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
